import { X } from "lucide-react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Service } from "../types/service.type"
import { getReadablePrimaryButtonStyle } from "../utils/buttonColor"

export type ServiceCategoryTab = { id: string; label: string }

type ServicePickerModalProps = {
  isOpen: boolean
  onClose: () => void
  themeColor: string
  buttonColor: string
  fieldClass: string
  selectedServices: Service[]
  filteredServices: Service[]
  serviceCategoryTabs: ServiceCategoryTab[]
  serviceCategoryTab: string
  onCategoryTabChange: (tabId: string) => void
  serviceSearchQuery: string
  onSearchQueryChange: (query: string) => void
  onToggleService: (service: Service) => void
}

export function ServicePickerModal({
  isOpen,
  onClose,
  themeColor,
  buttonColor,
  fieldClass,
  selectedServices,
  filteredServices,
  serviceCategoryTabs,
  serviceCategoryTab,
  onCategoryTabChange,
  serviceSearchQuery,
  onSearchQueryChange,
  onToggleService,
}: ServicePickerModalProps) {
  const { t } = useTranslation(['booking', 'common'])

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener("keydown", onKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center md:items-center md:p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label={t('booking:closePicker')}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-picker-title"
        className="relative z-[101] flex w-full max-h-[85dvh] min-h-0 flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl md:max-h-[80dvh] md:max-w-lg md:rounded-2xl"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h4 id="service-picker-title" className="text-base font-bold">
              {t('booking:pickerTitle')}
            </h4>
            <p className="text-xs text-gray-500">
              {t('booking:pickerSubtitle', { count: selectedServices.length })}
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
            aria-label={t('common:close')}
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="shrink-0 border-b border-gray-100 px-2 pb-2 pt-1">
          <div
            className="flex gap-1 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]"
            role="tablist"
            aria-label={t('booking:categoryTabs')}
          >
            {serviceCategoryTabs.map((tab) => {
              const isActive = serviceCategoryTab === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                    isActive ? "" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  style={isActive ? getReadablePrimaryButtonStyle(themeColor) : undefined}
                  onClick={() => onCategoryTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>
        <div className="shrink-0 border-b border-gray-100 px-4 py-2">
          <input
            type="search"
            value={serviceSearchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder={t('booking:pickerSearch')}
            className={`${fieldClass} !mt-0 bg-gray-50`}
          />
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
          {filteredServices.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-gray-500">
              {t('booking:pickerEmpty')}
            </li>
          ) : (
            filteredServices.map((service) => {
              const isChecked = selectedServices.some((s) => s.serviceId === service.serviceId)
              return (
                <li key={service.serviceId}>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg px-3 py-3 hover:bg-gray-100 active:bg-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300"
                      style={{ accentColor: themeColor }}
                      checked={isChecked}
                      onChange={() => onToggleService(service)}
                    />
                    <span className="text-left text-sm font-semibold leading-snug">{service.title}</span>
                  </label>
                </li>
              )
            })
          )}
        </ul>
        <div
          className="shrink-0 border-t border-gray-200 px-4 py-3"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
        >
          <button
            type="button"
            className="w-full rounded-lg py-3 text-sm font-bold"
            style={getReadablePrimaryButtonStyle(buttonColor)}
            onClick={onClose}
          >
            {t('booking:pickerDone')}
          </button>
        </div>
      </div>
    </div>
  )
}
